import { getNextSortingState } from "@/shared/utils/table-sorting";
import { SortDirection } from "@spz/shared-domain";
import type { SortingState } from "@tanstack/react-table";
import { parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";

export interface SortingConfig {
  onSortChange?: () => void;
}

export function useDataTableSorting(config?: SortingConfig) {
  // Sorting state with nuqs
  const sortStates = {
    sortField: parseAsString
      .withDefault("")
      .withOptions({ clearOnDefault: false }),
    sortDirection: parseAsStringLiteral([
      SortDirection.ASC,
      SortDirection.DESC,
    ]).withOptions({ clearOnDefault: false }),
  };

  const sortStatesOptions = {
    urlKeys: {
      sortField: "sortField",
      sortDirection: "sortDirection",
    },
  };

  const [sortValues, setRawSorting] = useQueryStates(
    sortStates,
    sortStatesOptions,
  );

  // Convert nuqs state to TanStack Table SortingState
  const sorting: SortingState = sortValues.sortField
    ? [
        {
          id: sortValues.sortField,
          desc: sortValues.sortDirection === SortDirection.DESC,
        },
      ]
    : [];

  const setSorting = (
    updaterOrValue: SortingState | ((prev: SortingState) => SortingState),
  ) => {
    const updatedSorting =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;

    const updatedSortingState = getNextSortingState(sorting, updatedSorting);

    setRawSorting(updatedSortingState);

    // Optional callback when sorting changes (e.g., reset pagination)
    config?.onSortChange?.();
  };

  return {
    sorting,
    setSorting,
    sortValues,
  };
}
